import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import SafeImage from './SafeImage';
import { getMediaType, toEmbedUrl } from '../../../utils/media';
import { backgroundEmbedIframeId, useEmbedStreamProgress } from '../../../utils/embedStreamProgress';

interface Track {
  id: number;
  title: string;
  artist: string;
  album: string;
  duration: string;
  image: string;
}

interface PlayerControlsProps {
  currentTrack: Track;
  audioUrl?: string | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  canPlay: boolean;
  playProgress: number;
  playCurrentTime: string;
  onSeek: (percent: number) => void;
  /** Called when the user dismisses embed playback (YouTube/SoundCloud). Parent can clear selection. */
  onCloseVideo?: () => void;
  /**
   * Single source of truth for hidden iframe “listen” mode (lifted to Home so playlist row and player stay in sync).
   */
  embedListenPlaying: boolean;
  setEmbedListenPlaying: Dispatch<SetStateAction<boolean>>;
}

export type PlayerControlsHandle = {
  /** Open the full video embed (Watch video / row video icon). */
  expandEmbed: () => void;
  /** Start/stop embed playback in a hidden iframe — playlist play without opening the video UI. */
  toggleBackgroundEmbed: () => void;
};

const PlayerControls = forwardRef<PlayerControlsHandle, PlayerControlsProps>(function PlayerControls(
  {
    currentTrack,
    audioUrl,
    isPlaying,
    onPlayPause,
    canPlay,
    playProgress,
    playCurrentTime,
    onSeek,
    onCloseVideo,
    embedListenPlaying,
    setEmbedListenPlaying,
  },
  ref
) {
  const [volume, setVolume] = useState(80);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  /** Collapsed (compact) bar — default so the player does not dominate the viewport. */
  const [isMinimized, setIsMinimized] = useState(true);
  /**
   * Incremented each time the user triggers playback so the iframe remounts
   * with autoplay. This avoids keeping a stale iframe from a previous track.
   */
  const [embedKey, setEmbedKey] = useState(0);
  const [bgEmbedKey, setBgEmbedKey] = useState(0);
  /** Only true when the user opens the full iframe (track row video icon). Chevron expand uses deluxe audio UI instead. */
  const [showVideoEmbed, setShowVideoEmbed] = useState(false);
  const prevTrackId = useRef(currentTrack.id);

  // Guard against stale preserved UI state; mount collapsed every fresh page load.
  useEffect(() => {
    setIsMinimized(true);
  }, []);

  // When the track changes, reset the embed so the new track starts fresh.
  // useLayoutEffect resets parent embedListenPlaying before paint when the track changes.
  useLayoutEffect(() => {
    if (prevTrackId.current !== currentTrack.id) {
      prevTrackId.current = currentTrack.id;
      setEmbedKey((k) => k + 1);
      setBgEmbedKey((k) => k + 1);
      setEmbedListenPlaying(false);
      setShowVideoEmbed(false);
    }
  }, [currentTrack.id, setEmbedListenPlaying]);

  const mediaType = getMediaType(audioUrl);
  const isEmbed = mediaType === 'youtube' || mediaType === 'soundcloud';
  const embedSrcVideo = isEmbed ? toEmbedUrl(audioUrl, 'default') : null;
  const embedSrcBg = isEmbed ? toEmbedUrl(audioUrl, 'background') : null;

  const embedStream = useEmbedStreamProgress({
    mediaType,
    embedListenPlaying,
    showVideoEmbed,
    bgEmbedKey,
  });

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSeek(Number(e.target.value));
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(Number(e.target.value));
  };

  /**
   * YouTube / SoundCloud: play/pause in the hidden background iframe (no video panel).
   * Matches playlist row behavior via toggleBackgroundEmbed on the ref.
   */
  const toggleEmbeddedAudioPlay = useCallback(() => {
    const mt = getMediaType(audioUrl);
    if (mt !== 'youtube' && mt !== 'soundcloud') return;
    setEmbedListenPlaying((prev) => {
      if (!prev) {
        setIsMinimized(true);
        setBgEmbedKey((k) => k + 1);
        return true;
      }
      setBgEmbedKey((k) => k + 1);
      return false;
    });
  }, [audioUrl, setEmbedListenPlaying]);

  /** Stop embed (unmount iframe), collapse bar, optionally clear track in parent. */
  const handleCloseVideo = () => {
    setEmbedListenPlaying(false);
    setShowVideoEmbed(false);
    setEmbedKey((k) => k + 1);
    setBgEmbedKey((k) => k + 1);
    setIsMinimized(true);
    onCloseVideo?.();
  };

  useImperativeHandle(
    ref,
    () => ({
      expandEmbed: () => {
        const mt = getMediaType(audioUrl);
        if (mt === 'youtube' || mt === 'soundcloud') {
          setEmbedListenPlaying(false);
          setBgEmbedKey((k) => k + 1);
          setShowVideoEmbed(true);
          setIsMinimized(false);
          setEmbedKey((k) => k + 1);
        }
      },
      toggleBackgroundEmbed: toggleEmbeddedAudioPlay,
    }),
    [audioUrl, setEmbedListenPlaying, toggleEmbeddedAudioPlay]
  );

  const noTrack = currentTrack.id === 0;

  const hiddenBackgroundIframe =
    embedListenPlaying && embedSrcBg && !showVideoEmbed ? (
      <div
        className="pointer-events-none fixed left-0 top-0 z-0 h-px w-px max-h-px max-w-px overflow-hidden opacity-0"
        aria-hidden
      >
        <iframe
          id={backgroundEmbedIframeId(bgEmbedKey)}
          key={bgEmbedKey}
          src={embedSrcBg}
          title={currentTrack.title ? `${currentTrack.title} (audio)` : 'Audio'}
          allow="autoplay; encrypted-media; picture-in-picture"
          className="h-px w-px border-0"
        />
      </div>
    ) : null;

  // ── Collapsed / minimised bar ───────────────────────────────────────────────
  if (isMinimized) {
    return (
      <>
      <div className="fixed bottom-14 md:bottom-0 left-0 right-0 z-40 border-t border-stone-800/60 bg-stone-950/95 px-3 py-1.5 text-white shadow-[0_-8px_32px_rgba(0,0,0,0.35)] backdrop-blur-xl md:px-6">
        <div className="mx-auto flex max-w-7xl items-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => {
              setShowVideoEmbed(false);
              setIsMinimized(false);
            }}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-stone-400 transition-colors hover:bg-white/10 hover:text-amber-300"
            title="Expand player"
            aria-expanded={false}
            aria-label="Expand player"
          >
            <i className="ri-arrow-up-s-line text-xl" aria-hidden />
          </button>
          {noTrack ? (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10">
              <i className="ri-music-line text-stone-300" />
            </div>
          ) : (
            <SafeImage src={currentTrack.image} alt={currentTrack.title} size="sm2" />
          )}
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-sm font-medium">{noTrack ? 'No track selected' : currentTrack.title}</h3>
            <p className="truncate text-xs text-stone-400">
              {noTrack
                ? 'Expand for full controls'
                : isEmbed
                ? `${currentTrack.artist} · ${mediaType === 'youtube' ? 'YouTube' : 'SoundCloud'}${embedListenPlaying ? ' · Playing audio' : ''}`
                : currentTrack.artist}
            </p>
          </div>
          {/* For embed tracks, main play = background audio. Chevron expands to deluxe audio (no video iframe). */}
          {isEmbed ? (
            <div className="flex shrink-0 items-center gap-1">
              {!noTrack && (
                <button
                  type="button"
                  onClick={handleCloseVideo}
                  className="flex h-9 w-9 items-center justify-center rounded-xl text-stone-400 transition-colors hover:bg-white/10 hover:text-amber-200"
                  title="Stop and clear"
                  aria-label="Stop playback and return to the app"
                >
                  <i className="ri-close-line text-xl" aria-hidden />
                </button>
              )}
              <button
                type="button"
                onClick={toggleEmbeddedAudioPlay}
                disabled={noTrack}
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  canPlay
                    ? 'bg-amber-400 text-stone-950 hover:bg-amber-300'
                    : 'cursor-not-allowed bg-white/10 text-stone-500'
                }`}
                title={isPlaying ? 'Pause' : 'Play'}
                aria-label={isPlaying ? 'Pause' : 'Play'}
              >
                <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={canPlay ? onPlayPause : undefined}
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                canPlay
                  ? 'bg-amber-400 text-stone-950 hover:bg-amber-300'
                  : 'cursor-not-allowed bg-white/10 text-stone-500'
              }`}
              title={canPlay ? (isPlaying ? 'Pause' : 'Play') : 'No audio preview'}
              aria-label={canPlay ? (isPlaying ? 'Pause' : 'Play') : 'No audio preview'}
            >
              <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`} />
            </button>
          )}
        </div>
      </div>
      {hiddenBackgroundIframe}
      </>
    );
  }

  // ── Expanded player ─────────────────────────────────────────────────────────
  return (
    <>
    <div className="fixed bottom-14 md:bottom-0 left-0 right-0 z-40 border-t border-stone-800/60 bg-stone-950/95 px-4 py-3 text-white shadow-[0_-8px_32px_rgba(0,0,0,0.35)] backdrop-blur-xl md:px-6">
      <div className="mx-auto max-w-7xl">
        {isEmbed && !noTrack && showVideoEmbed && (
          <div className="mb-2">
            <button
              type="button"
              onClick={handleCloseVideo}
              className="flex w-full max-w-[min(100%,28rem)] mx-auto items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/15 px-4 py-2.5 text-sm font-medium text-amber-100 transition-colors hover:bg-amber-500/25"
              title="Stop playback and return to the app"
              aria-label="Close video and return to the app"
            >
              <i className="ri-close-line text-lg" aria-hidden />
              Close video
            </button>
          </div>
        )}
        <div className="mb-2 flex justify-center">
          <button
            type="button"
            onClick={() => {
              setShowVideoEmbed(false);
              setIsMinimized(true);
            }}
            className="flex h-8 w-full max-w-[min(100%,28rem)] items-center justify-center gap-2 rounded-xl text-stone-400 transition-colors hover:bg-white/5 hover:text-stone-200"
            title="Collapse player"
            aria-expanded
            aria-label="Collapse player"
          >
            <i className="ri-arrow-down-s-line text-lg" aria-hidden />
            <span className="text-xs font-medium">Collapse</span>
          </button>
        </div>

        <div className="flex items-center gap-3 mb-3">
          {noTrack ? (
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
              <i className="ri-music-line text-stone-300 text-xl" />
            </div>
          ) : (
            <SafeImage src={currentTrack.image} alt={currentTrack.title} size="sm" />
          )}
          <div className="flex-1 min-w-0">
            <h3 className="truncate text-sm font-medium">{noTrack ? 'No track selected' : currentTrack.title}</h3>
            <p className="truncate text-xs text-stone-400">
              {noTrack
                ? 'Choose from playlist or discover'
                : isEmbed && !showVideoEmbed
                  ? `${currentTrack.artist} · ${mediaType === 'youtube' ? 'YouTube' : 'SoundCloud'} · Audio`
                  : currentTrack.artist}
            </p>
          </div>
          <button className="flex h-8 w-8 items-center justify-center text-stone-400">
            <i className="ri-heart-line text-lg" />
          </button>
        </div>

        {/* ── Full video embed (only from track row video icon) ── */}
        {isEmbed && embedSrcVideo && showVideoEmbed ? (
          <div className="relative mb-3 overflow-hidden rounded-xl bg-stone-900 ring-1 ring-white/10">
            <button
              type="button"
              onClick={handleCloseVideo}
              className="absolute right-2 top-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-stone-950/90 text-stone-100 shadow-md ring-1 ring-white/20 transition hover:bg-stone-800"
              title="Close video"
              aria-label="Close video"
            >
              <i className="ri-close-line text-xl" aria-hidden />
            </button>
            <iframe
              key={embedKey}
              src={embedSrcVideo}
              title={currentTrack.title}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className={`w-full border-0 ${
                mediaType === 'youtube' ? 'aspect-video' : 'h-[166px]'
              }`}
            />
          </div>
        ) : (
          /* ── Standard / deluxe audio progress (embed: time from iframe API; no seek) ── */
          <div className="mb-3">
            <div className="mb-1 flex items-center gap-2 text-xs text-stone-400">
              <span>
                {isEmbed && !showVideoEmbed
                  ? embedListenPlaying
                    ? embedStream.currentLabel
                    : '0:00'
                  : playCurrentTime}
              </span>
              <div className="flex-1 relative">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={isEmbed && !showVideoEmbed ? embedStream.progress : playProgress}
                  onChange={handleProgressChange}
                  disabled={!canPlay || (isEmbed && !showVideoEmbed)}
                  className="w-full h-1 appearance-none rounded-lg bg-white/10 cursor-pointer slider disabled:cursor-not-allowed disabled:opacity-60"
                  style={{
                    background: `linear-gradient(to right, #fbbf24 0%, #fbbf24 ${
                      isEmbed && !showVideoEmbed ? embedStream.progress : playProgress
                    }%, rgba(255,255,255,0.12) ${
                      isEmbed && !showVideoEmbed ? embedStream.progress : playProgress
                    }%, rgba(255,255,255,0.12) 100%)`,
                  }}
                />
              </div>
              <span>
                {isEmbed && !showVideoEmbed
                  ? embedListenPlaying
                    ? embedStream.durationLabel
                    : '—'
                  : currentTrack.duration || '0:00'}
              </span>
            </div>
            {isEmbed && !showVideoEmbed && (
              <p className="text-center text-[11px] text-stone-500">
                {embedListenPlaying ? 'Streaming — scrubbing not available for YouTube / SoundCloud' : 'Press play to stream'}
              </p>
            )}
          </div>
        )}

        {/* ── Transport: direct audio, or embed deluxe audio (not full video iframe) ── */}
        {(!isEmbed || (isEmbed && !showVideoEmbed)) && (
          <div className="flex items-center justify-between">
            <button
              type="button"
              className={`flex h-8 w-8 items-center justify-center text-stone-400 ${isEmbed ? 'opacity-40' : ''}`}
              disabled={isEmbed}
              title={isEmbed ? 'Not available for streaming' : 'Shuffle'}
              aria-label={isEmbed ? 'Not available for streaming' : 'Shuffle'}
            >
              <i className="ri-shuffle-line text-lg" />
            </button>
            <button
              type="button"
              className={`flex h-10 w-10 items-center justify-center text-stone-200 ${isEmbed ? 'opacity-40' : ''}`}
              disabled={!!isEmbed}
              title={isEmbed ? 'Not available for streaming' : 'Previous'}
              aria-label={isEmbed ? 'Not available for streaming' : 'Previous'}
            >
              <i className="ri-skip-back-fill text-xl" />
            </button>
            <button
              type="button"
              onClick={
                isEmbed
                  ? canPlay
                    ? toggleEmbeddedAudioPlay
                    : undefined
                  : canPlay
                    ? onPlayPause
                    : undefined
              }
              className={`flex h-12 w-12 items-center justify-center rounded-full ${
                canPlay
                  ? 'bg-amber-400 text-stone-950 hover:bg-amber-300'
                  : 'bg-white/10 text-stone-500 cursor-not-allowed'
              }`}
              title={canPlay ? (isPlaying ? 'Pause' : 'Play') : 'No audio preview'}
              aria-label={canPlay ? (isPlaying ? 'Pause' : 'Play') : 'No audio preview'}
            >
              <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-xl`} />
            </button>
            <button
              type="button"
              className={`flex h-10 w-10 items-center justify-center text-stone-200 ${isEmbed ? 'opacity-40' : ''}`}
              disabled={!!isEmbed}
              title={isEmbed ? 'Not available for streaming' : 'Next'}
              aria-label={isEmbed ? 'Not available for streaming' : 'Next'}
            >
              <i className="ri-skip-forward-fill text-xl" />
            </button>
            <div className="relative">
              <button
                type="button"
                onClick={isEmbed ? undefined : () => setShowVolumeSlider(!showVolumeSlider)}
                className={`flex h-8 w-8 items-center justify-center text-stone-400 ${isEmbed ? 'opacity-40' : ''}`}
                disabled={!!isEmbed}
                title={isEmbed ? 'Not available for streaming' : 'Volume'}
                aria-label={isEmbed ? 'Not available for streaming' : 'Volume'}
              >
                <i className="ri-volume-up-line text-lg" />
              </button>
              {!isEmbed && showVolumeSlider && (
                <div className="absolute bottom-10 right-0 w-32 rounded-2xl border border-stone-700 bg-stone-900 p-3 shadow-lg">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-full h-1 appearance-none rounded-lg bg-white/10 cursor-pointer"
                    style={{
                      background: `linear-gradient(to right, #fbbf24 0%, #fbbf24 ${volume}%, rgba(255,255,255,0.12) ${volume}%, rgba(255,255,255,0.12) 100%)`,
                    }}
                  />
                  <div className="mt-1 text-center text-xs text-stone-300">{volume}%</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
    {hiddenBackgroundIframe}
    </>
  );
});

export default PlayerControls;
